import type { CombinedFilter, FilterSpec } from '../types/compute';
import type { AlgorithmInput } from './batchRequestBuilder';
import { periodOrFilterId, withAutoCombined } from './filterSpec';
import { withoutMarkerConjunct } from './tonnageMode';

/**
 * The `error` chart's "Ley de corte" (SPEC/2423).
 *
 * `AlgoritmoError` takes no cut-off: it sums every block of each category. The Lib
 * does gate blocks by the per-model filters before any algorithm runs, though, so
 * the cut-off lowers to one `continuous` filter per model on that model's compared
 * grade column (`value_columns_{m+1}`), `min_limit_value = c` and no max —
 * FiltroContinuous reads an absent max as +inf, so the run counts `grade >= c`.
 *
 * The filter IS the persisted form: the wire carries no cut-off key. The draft's
 * `errorCutoff` exists only between load and build, the same way the tonnage
 * split's `unique_categories` does (see expandTonnageByCategory).
 */

const ERROR = 'error';
const GRADE_COL_PARAM = ['value_columns_1', 'value_columns_2'] as const;

/** Its own id namespace, distinct from a user's cards (`m{n}f{k}`) and the tonnage
 *  split (`m{n}cat{k}`), so recognising it on the way back is a lookup. */
const MARKER_ID = /^m\d+cutoff$/;
/** A combined the lowering still owns: a bare AND chain (filterSpec's PLAIN_AND). */
const PLAIN_AND = /^\s*[A-Za-z0-9_]+(\s+AND\s+[A-Za-z0-9_]+)*\s*$/;

export function isErrorCutoffFilterId(id: string): boolean {
  return MARKER_ID.test(id);
}

/** Build-time: lower the draft's cut-off into each model's filters. */
export function expandErrorCutoff(a: AlgorithmInput): AlgorithmInput {
  if (a.name !== ERROR || typeof a.errorCutoff !== 'number' || !Number.isFinite(a.errorCutoff)) {
    return a;
  }
  const cutoff = a.errorCutoff;
  const out: AlgorithmInput = {
    ...a,
    models: a.models.map((m, mi) => {
      const column = a.parameters[GRADE_COL_PARAM[mi]];
      // No grade column, nothing to filter on. The execute gate already refuses
      // this state (value_columns_1/2 are required), so no run is affected.
      if (typeof column !== 'number') return m;
      const marker: FilterSpec = {
        id: `m${mi}cutoff`,
        type: 'continuous',
        continuous_column: column,
        min_limit_value: cutoff,
      };
      return { ...m, filters: withCutoffMarker(m.filters ?? [], marker, mi) };
    }),
  };
  delete out.errorCutoff;
  return out;
}

/**
 * The model's selector: its top-level combined. The periods-of-interest OR-group
 * is a combined too, but it is nested inside that selector, never it, unless it is
 * the only combined there is (see tonnageMode's withSplitFilter).
 */
function topSelectorIndex(filters: readonly FilterSpec[], mi: number): number {
  return filters.findIndex((f) => f.type === 'combined' && f.id !== periodOrFilterId(mi));
}

/**
 * AND the marker into one model's selector so the run really is filtered.
 *
 * An existing selector is the user's, whatever it looks like — including a plain
 * AND chain that leaves a card out (`m0f1 AND m0f2` over three cards, reachable
 * because withAutoCombined only re-syncs on card add/remove). So it is never
 * regenerated: the marker is appended to its text, after parenthesising anything
 * that is not a plain chain, and the card keeps its place in the list. Only a model
 * with no selector gets one minted, by withAutoCombined, exactly as a card would.
 * (tonnageMode's withSplitFilter still regenerates a plain chain; see SPEC/2423.)
 */
function withCutoffMarker(present: FilterSpec[], marker: FilterSpec, mi: number): FilterSpec[] {
  const at = topSelectorIndex(present, mi);
  if (at < 0) return withAutoCombined([...present, marker], mi);
  const selector = present[at] as CombinedFilter;
  const expr = selector.expression;
  // The user's text is kept byte for byte (spacing included), so the inverse can
  // take back exactly the suffix added here. An empty expression already blocks the
  // run (algorithmValidity), but the marker must still be referenced: a card the
  // selector never names is dropped on load, and the cut-off with it.
  const expression = !expr.trim()
    ? marker.id
    : PLAIN_AND.test(expr)
      ? `${expr} AND ${marker.id}`
      : `(${expr}) AND ${marker.id}`;
  return [...present.slice(0, at), marker, { ...selector, expression }, ...present.slice(at + 1)];
}

/** Load-time inverse of {@link expandErrorCutoff}: drop the markers, restore the
 *  user's own selector, and lift the cut-off back into `errorCutoff`. */
export function collapseErrorCutoff(a: AlgorithmInput): AlgorithmInput {
  if (a.name !== ERROR) return a;
  let cutoff: number | undefined;
  let found = false;
  const models = a.models.map((m, mi) => {
    const present = m.filters ?? [];
    const markers = present.filter((f) => isErrorCutoffFilterId(f.id));
    if (markers.length === 0) return m;
    found = true;
    const own = markers[0];
    if (cutoff === undefined && own.type === 'continuous') cutoff = own.min_limit_value;
    const filters = withoutCutoffMarker(present, mi);
    // A model whose only card was the marker returns to the shape it was drafted
    // in: no `filters` key rather than an empty list.
    if (filters.length > 0) return { ...m, filters };
    const bare = { ...m };
    delete bare.filters;
    return bare;
  });
  if (!found) return a;
  const out: AlgorithmInput = { ...a, models };
  if (cutoff !== undefined) out.errorCutoff = cutoff;
  return out;
}

/** Undo {@link withCutoffMarker} on one model: drop the marker card and take its
 *  token back out of the selector's text. A selector left with one operand or none
 *  was minted by the lowering, so withAutoCombined drops it again; anything longer
 *  is the user's and stays exactly as they wrote it. */
function withoutCutoffMarker(present: FilterSpec[], mi: number): FilterSpec[] {
  const kept = present.filter((f) => !isErrorCutoffFilterId(f.id));
  const at = topSelectorIndex(kept, mi);
  if (at < 0) return withAutoCombined(kept, mi);
  const selector = kept[at] as CombinedFilter;
  const put = (expression: string) => [
    ...kept.slice(0, at),
    { ...selector, expression },
    ...kept.slice(at + 1),
  ];
  const marker = `m${mi}cutoff`;
  // An empty selector the marker was put into (see withCutoffMarker).
  if (selector.expression === marker) return put('');
  const unwrapped = withoutMarkerConjunct(selector, MARKER_ID) as CombinedFilter;
  if (unwrapped !== selector) return put(unwrapped.expression);
  if (!PLAIN_AND.test(selector.expression)) return kept;
  // The literal suffix withCutoffMarker appended, taken back exactly.
  const suffix = ` AND ${marker}`;
  if (selector.expression.endsWith(suffix)) {
    const restored = selector.expression.slice(0, -suffix.length);
    return /\s+AND\s+/.test(restored.trim()) ? put(restored) : withAutoCombined(put(restored), mi);
  }
  // Otherwise the chain was minted by withAutoCombined, which may put the marker
  // before the periods-of-interest operand (`m0cutoff AND m0periodOr`).
  const operands = selector.expression
    .trim()
    .split(/\s+AND\s+/)
    .filter((t) => t !== marker);
  return withAutoCombined(put(operands.join(' AND ')), mi);
}

/**
 * The selector a model had BEFORE the cut-off was ANDed in, for labelling.
 *
 * With a single user card, the lowering has to mint a combined (`m0f2 = "m0f1 AND
 * m0cutoff"`) just to hold the marker, and that id is one the user never wrote and
 * never sees (collapseErrorCutoff drops it on load). Their one card is the answer.
 * Every other shape keeps its id: a hand-written selector is theirs, and an
 * auto-maintained chain over 2+ cards gets the same id with or without the marker.
 */
export function selectorWithoutCutoff(
  id: string,
  filters: readonly FilterSpec[],
  modelIndex: number
): string {
  const def = filters.find((f) => f.id === id);
  if (def?.type !== 'combined' || !PLAIN_AND.test(def.expression)) return id;
  // The marker is not always last: withAutoCombined puts the periods-of-interest
  // operand after every other leaf (`m0cutoff AND m0periodOr`).
  const tokens = def.expression.trim().split(/\s+AND\s+/);
  const rest = tokens.filter((t) => t !== `m${modelIndex}cutoff`);
  return rest.length === 1 && rest.length < tokens.length ? rest[0] : id;
}

/** Wire shape: the cut-off one executed `error` entry ran with, if any. */
export function executedErrorCutoff(entry: {
  name: string;
  filters?: FilterSpec[];
}): number | undefined {
  if (entry.name !== ERROR) return undefined;
  const marker = (entry.filters ?? []).find((f) => isErrorCutoffFilterId(f.id));
  return marker?.type === 'continuous' ? marker.min_limit_value : undefined;
}

/** A combined's expression with the cut-off marker taken back out, for labelling:
 *  the `(<expr>) AND m{n}cutoff` wrapper put on a hand-written selector, or a
 *  marker operand of a plain AND chain — the two shapes {@link withCutoffMarker}
 *  and withAutoCombined produce. Any other expression comes back as written. */
export function expressionWithoutCutoff(f: CombinedFilter): string {
  const unwrapped = withoutMarkerConjunct(f, MARKER_ID) as CombinedFilter;
  if (unwrapped !== f) return unwrapped.expression;
  if (!PLAIN_AND.test(f.expression)) return f.expression;
  return f.expression
    .trim()
    .split(/\s+AND\s+/)
    .filter((t) => !isErrorCutoffFilterId(t))
    .join(' AND ');
}
